"use client";

import { useState } from "react";
import { useTheme } from "next-themes";
import { useTranslations, useLocale } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { useSession } from "@/hooks/use-session";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { useSiteFlavor } from "@/components/layout/site-flavor-provider";
import posthog from "posthog-js";

export function LandingNav() {
  const t = useTranslations("landing");
  const locale = useLocale();
  const { isAuthenticated } = useSession();
  const { isGetmint } = useSiteFlavor();
  const { resolvedTheme } = useTheme();
  const ctaHref = isAuthenticated ? "/new" : "/sign-up";
  const [loading, setLoading] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-landing-border/50 bg-landing-bg/80 backdrop-blur-xl">
      <div className="mx-auto flex h-auto min-h-16 max-w-7xl items-center justify-between px-6 py-2">
        <Link href="/" className="flex items-center transition-opacity hover:opacity-80">
          <img
            src={locale === "ru" ? "/logo-mint24-icon.png" : "/logo-mint24-en.png"}
            alt="Mint24"
            className={cn("h-7", resolvedTheme === "dark" && "invert")}
          />
        </Link>

        {/* Desktop: single row */}
        <div className="hidden sm:flex items-center gap-3">
          <LangSwitcher />
          {!isGetmint && (
          <a
            href="https://t.me/mintai_content_bot?start=support"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => posthog.capture("landing_click", { element: "support_nav" })}
            className="text-sm font-medium text-landing-muted transition-colors hover:text-ink"
          >
            {t("nav.support")}
          </a>
          )}
          <ThemeToggle />
          <Link
            href={ctaHref}
            onPointerDown={() => setLoading(true)}
            onClick={() => {
              posthog.capture("landing_click", { element: "cta_nav" });
              if (!isAuthenticated) posthog.capture("sign_in_clicked", { source: "nav" });
            }}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-mint px-5 py-2 text-sm font-semibold text-[#052e25] transition-all hover:bg-mint-dark active:scale-95 active:opacity-80 touch-manipulation"
            style={{ minWidth: "120px" }}
          >
            {loading ? (
              <>
                <span className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border-2 border-[#052e25]/30 border-t-[#052e25] animate-spin" />
                <span>{t("nav.ctaLoading")}</span>
              </>
            ) : (
              t("nav.tryFree")
            )}
          </Link>
        </div>

        {/* Mobile: support + CTA only */}
        <div className="flex sm:hidden items-center gap-2">
          {!isGetmint && (
          <a
            href="https://t.me/mintai_content_bot?start=support"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => posthog.capture("landing_click", { element: "support_nav" })}
            className="text-sm font-medium text-landing-muted transition-colors hover:text-ink"
          >
            {t("nav.support")}
          </a>
          )}
          <Link
            href={ctaHref}
            onPointerDown={() => setLoading(true)}
            onClick={() => {
              posthog.capture("landing_click", { element: "cta_nav" });
              if (!isAuthenticated) posthog.capture("sign_in_clicked", { source: "nav" });
            }}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-mint px-5 py-2 text-sm font-semibold text-[#052e25] transition-all hover:bg-mint-dark active:scale-95 active:opacity-80 touch-manipulation"
            style={{ minWidth: "120px" }}
          >
            {loading ? (
              <>
                <span className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border-2 border-[#052e25]/30 border-t-[#052e25] animate-spin" />
                <span>{t("nav.ctaLoading")}</span>
              </>
            ) : (
              t("nav.tryFree")
            )}
          </Link>
        </div>
      </div>
    </nav>
  );
}

export function LangSwitcher() {
  const locale = useLocale();
  const pathname = usePathname();
  const { isGetmint } = useSiteFlavor();
  if (isGetmint) return null;

  return (
    <div className="flex rounded-full border border-landing-border bg-landing-card p-0.5">
      <Link
        href={pathname}
        locale="ru"
        onClick={() => posthog.capture("landing_click", { element: "lang_ru" })}
        className={cn(
          "rounded-full px-3 py-1 text-xs font-medium transition-colors",
          locale === "ru"
            ? "bg-ink/10 text-ink"
            : "text-landing-muted hover:text-ink"
        )}
      >
        RU
      </Link>
      <Link
        href={pathname}
        locale="en"
        onClick={() => posthog.capture("landing_click", { element: "lang_en" })}
        className={cn(
          "rounded-full px-3 py-1 text-xs font-medium transition-colors",
          locale === "en"
            ? "bg-ink/10 text-ink"
            : "text-landing-muted hover:text-ink"
        )}
      >
        EN
      </Link>
    </div>
  );
}
